import { supabase } from '@/integrations/supabase/client';
import { sanitizeFileName } from './security';

const PRODUCT_IMAGES_BUCKET = 'product-images';
const CATEGORY_IMAGES_BUCKET = 'category-images';

import placeholderEmpty from '@/assets/placeholder-empty.jpg';

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
}

export class ImageManager {
  // Upload image file to Supabase storage
  static async uploadImage(file: File, bucket: string = PRODUCT_IMAGES_BUCKET): Promise<ImageUploadResult> {
    try {
      // Validate file
      if (!file.type.startsWith('image/')) {
        return { success: false, error: 'קובץ לא תקין - יש להעלות תמונה בלבד' };
      }

      if (file.size > 5 * 1024 * 1024) {
        return { success: false, error: 'גודל הקובץ לא יכול לעלות על 5MB' };
      }

      // Generate secure filename
      const fileExt = file.name.split('.').pop();
      const fileName = sanitizeFileName(file.name);
      const filePath = `${Date.now()}-${fileName}.${fileExt}`;

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file);

      if (error) {
        console.error('Upload error:', error);
        return { success: false, error: 'שגיאה בהעלאת התמונה' };
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path);

      return {
        success: true,
        url: urlData.publicUrl,
        path: data.path
      };
    } catch (error) {
      console.error('Image upload error:', error);
      return { success: false, error: 'שגיאה בהעלאת התמונה' };
    }
  }

  // Delete image from Supabase storage
  static async deleteImage(imagePath: string, bucket: string = PRODUCT_IMAGES_BUCKET): Promise<boolean> {
    try {
      // Extract path from URL if needed
      const path = ImageManager.extractPathFromUrl(imagePath);
      if (!path) return true; // If no path, consider it deleted

      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) {
        console.error('Delete error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Image delete error:', error);
      return false;
    }
  }

  // Extract storage path from Supabase URL
  static extractPathFromUrl(url: string): string | null {
    if (!url || url.startsWith('/') || url.startsWith('data:')) {
      return null;
    }

    // Extract path from Supabase URL
    const match = url.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+)$/);
    return match ? match[1] : null;
  }

  // Get display URL for image - simplified to show default placeholders
  static getImageUrl(imagePath: string | null, type: 'product' | 'category' = 'product'): string {
    // Return empty placeholder if no path
    if (!imagePath || imagePath.trim() === '') {
      return placeholderEmpty;
    }

    // If it's already a full URL or starts with /, use it directly
    if (imagePath.startsWith('http') || imagePath.startsWith('/')) {
      return imagePath;
    }

    // If it's a base64 data URL, use it directly (for temporary display)
    if (imagePath.startsWith('data:')) {
      return imagePath;
    }

    // Otherwise, get public URL from Supabase
    const bucket = type === 'category' ? CATEGORY_IMAGES_BUCKET : PRODUCT_IMAGES_BUCKET;
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(imagePath);
      
    return data.publicUrl;
  }

  // Handle image error fallback - simplified to show empty placeholder
  static handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement>,
    type: 'product' | 'category' = 'product'
  ) => {
    const target = e.target as HTMLImageElement;
    
    if (target.src !== placeholderEmpty) {
      target.src = placeholderEmpty;
    }
  };
}
